import type { CellRef, FormulaExpr, Scalar, WorkbookPayload } from '../models.ts';

type Sheet = WorkbookPayload['sheets'][number];

// Spreadsheet addresses are display only: column letters and row numbers are
// the cell's place in the sheet's declared order. Identity stays the cell id.
export function columnLetters(index: number): string {
  let n = index;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

export type Addressing = {
  /** A1-style address of a cell id on its sheet, or null if the cell is not on it. */
  address(sheetId: string, cellId: string): string | null;
  /** Zero-based row and column of a cell id on its sheet, or null if it is not on it. */
  coords(sheetId: string, cellId: string): { row: number; col: number } | null;
  sheetTitle(sheetId: string): string | null;
};

export function addressing(workbook: WorkbookPayload): Addressing {
  const places = new Map<string, Map<string, string>>();
  const grid = new Map<string, Map<string, { row: number; col: number }>>();
  for (const sheet of workbook.sheets) {
    const rows = new Map(sheet.rowIds.map((id, i) => [id, i + 1]));
    const columns = new Map(sheet.columnIds.map((id, i) => [id, columnLetters(i)]));
    const byCell = new Map<string, string>();
    const byCoords = new Map<string, { row: number; col: number }>();
    const colIndex = new Map(sheet.columnIds.map((id, i) => [id, i]));
    for (const cell of sheet.cells) {
      const row = rows.get(cell.rowId);
      const column = columns.get(cell.columnId);
      const col = colIndex.get(cell.columnId);
      if (row !== undefined && column !== undefined && col !== undefined) {
        byCell.set(cell.id, `${column}${row}`);
        byCoords.set(cell.id, { row: row - 1, col });
      }
    }
    places.set(sheet.id, byCell);
    grid.set(sheet.id, byCoords);
  }
  const titles = new Map(workbook.sheets.map((sheet: Sheet) => [sheet.id, sheet.title]));
  return {
    address: (sheetId, cellId) => places.get(sheetId)?.get(cellId) ?? null,
    coords: (sheetId, cellId) => grid.get(sheetId)?.get(cellId) ?? null,
    sheetTitle: (sheetId) => titles.get(sheetId) ?? null,
  };
}

const scalarText = (value: Scalar): string => {
  switch (value.kind) {
    case 'text':
      return `"${value.value.replaceAll('"', '""')}"`;
    case 'boolean':
      return value.value ? 'TRUE' : 'FALSE';
    case 'null':
      return '';
    default:
      return value.value;
  }
};

/**
 * The formula as the person would read it, built from the canonical AST and
 * never from stored text. A reference to another sheet carries the sheet title;
 * a reference that resolves to nothing stays visible as its id so nothing is
 * silently dropped.
 */
/**
 * A canonical range is an ordered list of cell ids, not a rectangle. It is shown as
 * A1:B3 only when that shorthand means exactly the same cells in the same order (the
 * full block, row by row, each once); anything else (sparse, reordered, duplicated or
 * unresolved) is shown as the explicit union (A1,A3), so a range never claims a cell
 * it does not contain. A one-cell range is that cell.
 */
function rangeText(
  sheetId: string,
  cellIds: string[],
  ref: (cell: CellRef) => string,
  places: Addressing,
): string {
  if (cellIds.length === 0) return '';
  const cell = (cellId: string): CellRef => ({ sheetId, cellId });
  if (cellIds.length === 1) return ref(cell(cellIds[0]));
  const at = cellIds.map((id) => places.coords(sheetId, id));
  if (at.every((c): c is { row: number; col: number } => c !== null)) {
    const rows = at.map((c) => c.row);
    const cols = at.map((c) => c.col);
    const [r0, r1] = [Math.min(...rows), Math.max(...rows)];
    const [c0, c1] = [Math.min(...cols), Math.max(...cols)];
    const exact =
      cellIds.length === (r1 - r0 + 1) * (c1 - c0 + 1) &&
      at.every(
        (c, i) =>
          c.row === r0 + Math.floor(i / (c1 - c0 + 1)) && c.col === c0 + (i % (c1 - c0 + 1)),
      );
    if (exact) {
      const a = ref(cell(cellIds[0]));
      const b = ref(cell(cellIds[cellIds.length - 1]));
      return `${a}:${b.slice(b.lastIndexOf('!') + 1)}`;
    }
  }
  return `(${cellIds.map((id) => ref(cell(id))).join(',')})`;
}

export function formulaText(expr: FormulaExpr, home: string, places: Addressing): string {
  const ref = (cell: CellRef) => {
    const address = places.address(cell.sheetId, cell.cellId) ?? cell.cellId;
    return cell.sheetId === home
      ? address
      : `${places.sheetTitle(cell.sheetId) ?? cell.sheetId}!${address}`;
  };
  const go = (node: FormulaExpr, nested: boolean): string => {
    switch (node.kind) {
      case 'literal':
        return scalarText(node.value);
      case 'cell':
        return ref(node.ref);
      case 'range':
        return rangeText(node.sheetId, node.cellIds, ref, places);
      case 'unary':
        return `${node.op}${go(node.arg, true)}`;
      case 'binary': {
        const text = `${go(node.left, true)}${node.op}${go(node.right, true)}`;
        return nested ? `(${text})` : text;
      }
      case 'call':
        return `${node.fn}(${node.args.map((arg) => go(arg, false)).join(',')})`;
    }
  };
  return `=${go(expr, false)}`;
}

/**
 * The ids of the cells on `sheetId` a formula reads (by reference or in a range), for
 * highlighting. Ids only: coordinates are not identity, and a reference to another
 * sheet is not on this one.
 */
export function referencedCells(expr: FormulaExpr, sheetId: string): Set<string> {
  const out = new Set<string>();
  const walk = (node: FormulaExpr): void => {
    switch (node.kind) {
      case 'cell':
        if (node.ref.sheetId === sheetId) out.add(node.ref.cellId);
        return;
      case 'range':
        if (node.sheetId === sheetId) for (const id of node.cellIds) out.add(id);
        return;
      case 'unary':
        return walk(node.arg);
      case 'binary':
        walk(node.left);
        return walk(node.right);
      case 'call':
        return node.args.forEach(walk);
      case 'literal':
        return;
    }
  };
  walk(expr);
  return out;
}
